import React, { useState } from "react";
import {
  FaHeart,
  FaUsers,
  FaMountain,
  FaLandmark,
  FaArrowRight,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { DestinationBrowseFilters } from "./destinationBrowseFilters";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";

const filters = [
  { label: "All", icon: null },
  { label: "Honeymoon", icon: <FaHeart className="text-brand-pink" /> },
  { label: "Family", icon: <FaUsers className="text-blue-500" /> },
  { label: "Adventure", icon: <FaMountain className="text-emerald-500" /> },
  { label: "Cultural", icon: <FaLandmark className="text-amber-600" /> },
] as const;

const INITIAL_SIZE = 7;
const PAGE_SIZE = 8;

type DestinationCollectionsProps = {
  filters: DestinationBrowseFilters;
};

const normalizeCountry = (value?: string | null) =>
  (value || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const canonicalCountry = (value?: string | null) => {
  const normalized = normalizeCountry(value);
  if (!normalized) return "";
  if (normalized === "uae" || normalized === "united arab emirates") {
    return "united arab emirates";
  }
  if (normalized === "in" || normalized === "india") return "india";
  return normalized;
};

const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "AE" ? "united arab emirates" : "india";
const isMatchingCountry = (country?: string | null) =>
  canonicalCountry(country) === siteCountry;

const getDestinationCountry = (destination: any) =>
  destination?.country ||
  destination?.leadCountry ||
  destination?.siteCountry ||
  destination?.sourceCountry ||
  destination?.market ||
  null;

const getMediaUrl = (value: any): string => {
  if (typeof value === "string") return value.trim();
  if (!value) return "";

  return (
    value.title_image ||
    value.titleImage ||
    value.mediaUrl ||
    value.imageUrl ||
    value.fileUrl ||
    value.assetUrl ||
    value.url ||
    value.image ||
    value.src ||
    value.thumbnailUrl ||
    getMediaUrl(value.media) ||
    ""
  ).trim();
};

const getDestinationImage = (destination: any) => {
  const media = destination?.media || {};
  const gallery = Array.isArray(media.gallery) ? media.gallery : [];
  const images = Array.isArray(media.images) ? media.images : [];

  return (
    getMediaUrl(media.title_image) ||
    getMediaUrl(media.titleImage) ||
    getMediaUrl(destination?.thumbnailUrl) ||
    getMediaUrl(destination?.heroImageUrl) ||
    getMediaUrl(gallery[0]) ||
    getMediaUrl(images[0]) ||
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80"
  );
};

const DestinationCollections: React.FC<DestinationCollectionsProps> = ({
  filters: browseFilters,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: destinations, loading } = useCMS<any[]>(() => cmsApi.getDestinations('isActive=true'));
  const [activeFilter, setActiveFilter] = useState<"All" | string>("All");
  const [visible, setVisible] = useState(INITIAL_SIZE);

  if (loading) return <div className="py-20 text-center">Loading...</div>;

  const allDestinations = destinations || [];
  const countryFiltered = allDestinations.filter((d: any) =>
    isMatchingCountry(getDestinationCountry(d))
  );

  const categoryFiltered =
    activeFilter === "All"
      ? countryFiltered
      : countryFiltered.filter((d: any) => {
          const cats: string[] = Array.isArray(d.categories)
            ? d.categories
            : d.category ? [d.category] : [];
          return cats.some((c) => c.toLowerCase() === activeFilter.toLowerCase());
        });

  const tabFiltered =
    browseFilters.tab === "Popular"
      ? categoryFiltered.filter((d: any) => d.isPopular)
      : browseFilters.tab === "New"
        ? categoryFiltered.filter((d: any) => d.isNew)
        : categoryFiltered;

 const regionFiltered =
    browseFilters.region === "All Regions"
      ? tabFiltered
      : tabFiltered.filter((d: any) =>
          ((d.continent || d.region || "").toLowerCase() === browseFilters.region.toLowerCase())
        );

 const travelTypeFiltered =
    browseFilters.travelType === "All Types"
      ? regionFiltered
      : regionFiltered.filter((d: any) =>
          (d.categories || []).some((c: string) => c.toLowerCase() === browseFilters.travelType.toLowerCase())
        );

const filtered =
    browseFilters.season === "Any Season"
      ? travelTypeFiltered
      : travelTypeFiltered.filter((d: any) => {
          const focus: string[] = Array.isArray(d.seasonFocus)
            ? d.seasonFocus
            : d.season ? [d.season] : [];
          return focus.some(
            (s) => s.toLowerCase() === browseFilters.season.toLowerCase()
          );
        });

  const handleFilterChange = (label: string) => {
    setActiveFilter(label);
    setVisible(INITIAL_SIZE);
  };

  return (
    <>
      <section className="overflow-hidden border-y border-gray-100 bg-white py-6 md:py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 sm:px-6 lg:flex-row lg:px-8">
          <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-widest text-gray-400 md:text-sm">
            {t("Collections:")}
          </span>

          <div className="flex flex-nowrap gap-3 overflow-x-auto md:gap-4">
            {filters.map((filter) => (
              <button
                key={filter.label}
                type="button"
                onClick={() => handleFilterChange(filter.label)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2 text-xs font-medium transition-all md:text-sm ${
                  activeFilter === filter.label
                    ? "bg-brand-dark text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {filter.icon}
                {t(filter.label)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="destination-collections" className="bg-brand-gray py-12 md:py-12">
        <div className="max-w-6xl mx-auto align-center px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 && (
            <div className="rounded-3xl border border-dashed border-brand-dark/10 bg-white p-10 text-center text-gray-500 shadow-sm">
              {t("No destinations match the selected filters right now. Try another combination.")}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-6">
            {filtered.slice(0, visible).map((destination: any, index: number) => {
              const isCompactCard = index === 1 || index === 2;

              return (
                <div
                  key={destination.id}
                  onClick={() => navigate(`/destination-detail/${destination.slug}`)}
                  className={`group relative cursor-pointer overflow-hidden rounded-3xl bg-white shadow-lg hover-lift ${
                    index === 0
                      ? "h-[500px] md:col-span-3 md:row-span-2"
                      : isCompactCard
                        ? "h-[235px]"
                        : "h-[340px]"
                  }`}
                >
                  <img
                    src={getDestinationImage(destination)}
                    alt={destination.name}
                    className="h-full w-full object-cover"
                  />

                  <div className="image-overlay absolute inset-0 opacity-80 transition-opacity group-hover:opacity-90" />

                  <div className="absolute inset-0 flex translate-y-4 flex-col justify-end p-6 text-left transition-transform duration-300 group-hover:translate-y-0 md:p-8">
                    {/* <h3
                      className={`font-heading font-bold text-white ${
                        isCompactCard ? "mb-1 line-clamp-1 text-2xl" : "mb-2 text-3xl"
                      }`}
                    >
                      {destination.name}
                    </h3>

                    <p
                      className={`text-sm text-gray-100 ${
                        isCompactCard
                          ? "mb-3 line-clamp-1 leading-5"
                          : "mb-4 line-clamp-3 leading-6"
                      }`}
                    >
                      {destination.shortDescription || destination.description}
                    </p> */}

                    <div className="mt-auto">
                      <h3
                      className={`font-heading font-bold text-white ${
                        isCompactCard ? "mb-1 line-clamp-1 text-2xl" : "mb-2 text-3xl"
                      }`}
                    >
                      {destination.name}
                    </h3>

                    {/* <p
                      className={`text-sm text-gray-100 ${
                        isCompactCard
                          ? "mb-3 line-clamp-1 leading-5"
                          : "mb-4 line-clamp-3 leading-6"
                      }`}
                    >
                      {destination.shortDescription || destination.description}
                    </p> */}
                      {/* <div className="flex items-center gap-4 text-sm text-white/80">
                        <span className="flex items-center gap-1">
                          <FaBoxOpen /> {t("Packages")}
                        </span>

                        {destination.rating && (
                          <span className="flex items-center gap-1">
                            <FaStar className="text-brand-accent" /> {destination.rating}
                          </span>
                        )}
                      </div> */}

                      <button
                        type="button"
                        className="mt-4 flex w-fit items-center gap-2 rounded-lg bg-white px-5 py-2 text-sm font-semibold text-brand-dark transition-colors hover:bg-brand-pink hover:text-white"
                      >
                        {t("View Details")} <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {visible < filtered.length && (
            <div className="mt-16 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((value) => value + PAGE_SIZE)}
                className="flex items-center gap-2 rounded-full border-2 border-brand-dark px-8 py-3 font-semibold text-brand-dark transition-all hover:bg-brand-dark hover:text-white"
              >
                {t("Load More Destinations")}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default DestinationCollections;
